import { Point, type Canvas, type TPointerEventInfo } from 'fabric';

type TTouchControllerParams = {
    canvas: Canvas;
    onViewportResize: (width: number, height: number) => void;
    panCallback: (opt: TPointerEventInfo) => void;
};

const PHONE_MAX_WIDTH_PX = 769;

// Behavior-preserving extraction of the native-touch wiring previously
// living in FabricScene.initListeners / dispose. Wraps two concerns
// that only apply on mobile:
//   1. two-finger touch → forwarded into the pan callback as a mock
//      TPointerEventInfo (identical to the legacy nativeTouchHandler).
//   2. phone-keyboard shown / hidden → surfaced via onViewportResize
//      (the World is free to interpret it as a resize()).
class TouchController {
    private readonly _canvas: Canvas;
    private readonly _canvasElement: HTMLCanvasElement | null;
    private readonly _nativeTouchHandler: (event: TouchEvent) => void;
    private readonly _phoneKeyboardHandler?: () => void;

    constructor(params: TTouchControllerParams) {
        this._canvas = params.canvas;
        this._canvasElement = this._canvas.getElement() ?? null;

        this._nativeTouchHandler = (event: TouchEvent) => {
            if (event.touches.length === 2) {
                const mockOpt = {
                    absolutePointer: new Point(0, 0),
                    e: event,
                    pointer: new Point(0, 0),
                    viewportPoint: new Point(0, 0),
                };
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                params.panCallback(mockOpt as any);
            }
        };

        if (this._canvasElement) {
            this._canvasElement.addEventListener('touchstart', this._nativeTouchHandler, { passive: false });
            this._canvasElement.addEventListener('touchmove', this._nativeTouchHandler, { passive: false });
            this._canvasElement.addEventListener('touchend', this._nativeTouchHandler, { passive: false });

            // Phone-keyboard show/hide triggers a visualViewport resize;
            // the World reacts by calling its own resize() via the callback.
            const { height, width } = window.visualViewport || { height: 0, width: 0 };
            this._phoneKeyboardHandler = () => {
                params.onViewportResize(width, height);
            };
            if (width < PHONE_MAX_WIDTH_PX) {
                window.visualViewport?.addEventListener('resize', this._phoneKeyboardHandler);
            }
        }
    }

    dispose(): void {
        if (this._canvasElement) {
            this._canvasElement.removeEventListener('touchstart', this._nativeTouchHandler);
            this._canvasElement.removeEventListener('touchmove', this._nativeTouchHandler);
            this._canvasElement.removeEventListener('touchend', this._nativeTouchHandler);
        }
        if (this._phoneKeyboardHandler) {
            window.visualViewport?.removeEventListener('resize', this._phoneKeyboardHandler);
        }
    }
}

export { TouchController };
